import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DATA_FETCH_STATUS } from '../../../../../../store/dashboard/constants';
import { fetchEpfFilingDetails } from '../../../../../../store/dashboard/compliancesBefe/service';
import { toEpfFilingDetails } from '../../../../../../store/dashboard/compliancesBefe/mappers';
import { loadCachedBefeData } from '../../../../../../store/dashboard/compliancesBefe/loadCachedBefeData';
import { BEFE_SERVICE_IDS, EPF_WINDOW_MONTHS } from '../../../../../../store/dashboard/compliancesBefe/constants';
import type { EpfFilingDetails, UwEpfFilingDetailsData } from '../../../../../../store/dashboard/compliancesBefe/types';
import { getEpfDetailsWindow } from '../../../../../../utils/dateUtils';
import type { EpfFilingDetailsState } from './types';

const IDLE_STATE: EpfFilingDetailsState = { paymentsByXValue: {}, establishments: [], status: 'idle' };
const UNAVAILABLE_STATE: EpfFilingDetailsState = { paymentsByXValue: {}, establishments: [], status: 'unavailable' };

/** One in-flight/settled request per `${orgId}|${startDate}|${endDate}`; failures are never kept. */
const detailsCache = new Map<string, Promise<EpfFilingDetails | null>>();

/**
 * The last epfCompliance chart object that invalidated the cache. Every completed epfCompliance
 * fetch produces a new chart object, so comparing references drops the key once per fetch, no
 * matter how many hook instances observe it.
 */
let lastInvalidatingChart: unknown = null;

/**
 * Per-payment EPF details for the EPF card's hover and establishments drawer, refreshed after
 * every completed epfCompliance fetch and never requested while that card is PROCESSING.
 */
export function useEpfFilingDetails(epfChart: any, enabled: boolean): EpfFilingDetailsState {
    const orgId = useParams().id;
    const [state, setState] = useState<EpfFilingDetailsState>(IDLE_STATE);

    useEffect(() => {
        const isSettled = !!epfChart?.isFetched && !epfChart?.isLoading;
        const isProcessing = epfChart?.metadata?.status === DATA_FETCH_STATUS.PROCESSING;
        if (!enabled || !orgId || !isSettled || isProcessing) return;

        const { startDate, endDate } = getEpfDetailsWindow(EPF_WINDOW_MONTHS);
        const key = `${orgId}|${startDate}|${endDate}`;
        if (lastInvalidatingChart !== epfChart) {
            lastInvalidatingChart = epfChart;
            detailsCache.delete(key);
        }
        evictOtherOrgs(orgId);

        let active = true;
        loadCachedBefeData<UwEpfFilingDetailsData, EpfFilingDetails>(
            detailsCache,
            key,
            () => fetchEpfFilingDetails(orgId, startDate, endDate),
            BEFE_SERVICE_IDS.EPF_FILING_DETAILS,
            toEpfFilingDetails,
        ).then((result) => {
            if (active) setState(result ? { ...result, status: 'ready' } : UNAVAILABLE_STATE);
        });
        return () => {
            active = false;
        };
    }, [enabled, orgId, epfChart]);

    return enabled ? state : IDLE_STATE;
}

/** Keeps only the current org's windows, so payloads of orgs viewed earlier in the tab are released. */
function evictOtherOrgs(orgId: string): void {
    const orgPrefix = `${orgId}|`;
    Array.from(detailsCache.keys())
        .filter((key) => !key.startsWith(orgPrefix))
        .forEach((key) => detailsCache.delete(key));
}
